/**
 * Day.js 플러그인 설정
 * - UTC 및 타임존 지원
 * - 다중 타임존 지원 (600+ IANA 타임존)
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'

// 플러그인 등록
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

/**
 * 사용자 타임존 조회
 * 1. 로컬 스토리지에서 확인 (사용자 수동 선택)
 * 2. 사용자 프로필에서 확인
 * 3. 브라우저 자동 감지 (모든 타임존 지원!)
 *
 * @returns {string} IANA 타임존 ID (예: "Asia/Seoul", "America/New_York")
 */
export const getUserTimezone = () => {
  // 1. 로컬 스토리지 확인 (사용자 수동 선택)
  const storedTimezone = localStorage.getItem('userTimezone')
  if (storedTimezone && isValidTimezone(storedTimezone)) {
    return storedTimezone
  }

  // 2. 사용자 프로필에서 확인
  // TODO: useAuthStore 연동
  // const userStore = useAuthStore()
  // if (userStore.user?.timezone && isValidTimezone(userStore.user.timezone)) {
  //   return userStore.user.timezone
  // }

  // 3. 브라우저 자동 감지 (모든 타임존 지원!)
  // America/New_York, Europe/London, Asia/Tokyo 등 600+ 타임존
  const detected = dayjs.tz.guess()

  // 4. 감지된 타임존 유효성 검증
  if (isValidTimezone(detected)) {
    return detected
  }

  // 5. 감지 실패 시에만 기본값
  console.warn('Timezone detection failed, using default: Asia/Seoul')
  return 'Asia/Seoul'
}

/**
 * 타임존 유효성 검증
 * @param {string} timezone - IANA 타임존 ID
 * @returns {boolean}
 */
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return true
  } catch (e) {
    return false
  }
}

/**
 * UTC 문자열을 사용자 타임존으로 변환
 * @param {string} utcString - ISO 8601 UTC 문자열
 * @param {string} format - 출력 형식 (기본: 'YYYY-MM-DD HH:mm:ss')
 * @param {string} timezone - 타임존 (기본: 사용자 타임존)
 * @returns {string}
 */
export const formatDate = (utcString, format = 'YYYY-MM-DD HH:mm:ss', timezone = null) => {
  if (!utcString) return '-'

  const targetTimezone = timezone || getUserTimezone()
  return dayjs.utc(utcString).tz(targetTimezone).format(format)
}

/**
 * 로컬 Date 객체를 UTC 문자열로 변환
 * @param {Date|string} localDate - 로컬 날짜
 * @returns {string} ISO 8601 UTC 문자열
 */
export const toUTC = (localDate) => {
  if (!localDate) return null
  return dayjs(localDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
}

/**
 * UTC 문자열을 로컬 Date 객체로 변환
 * @param {string} utcString - ISO 8601 UTC 문자열
 * @returns {Date}
 */
export const toLocalDate = (utcString) => {
  if (!utcString) return null
  return dayjs.utc(utcString).local().toDate()
}

export { dayjs }
